import { useContext, useEffect, useMemo } from "react";
import { useDebounce } from "hooks/lib-ui";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  QUERY_ADD_OFF_STREET_PARKING,
  QUERY_OFF_STREET_PARKING,
  QUERY_OFF_STREET_PARKINGS,
  QUERY_REMOVE_OFF_STREET_PARKING,
  QUERY_UPDATE_OFF_STREET_PARKING,
} from "services/web/api-cities.const";
import { CityContext } from "contexts/city-context-provider";
import { showGlobalLoading, hideGlobalLoading } from "hooks/use-global-loading";

export const useOffStreetParking = (bounding?: number[][] | undefined) => {
  const { currentCity } = useContext(CityContext);
  const nearBy = useDebounce(bounding, 500);

  const variables = useMemo(
    () => ({
      cityId: currentCity?.id ?? "",
      boundingBox: nearBy,
    }),
    [currentCity?.id, nearBy],
  );

  const { data, loading, refetch } = useQuery(QUERY_OFF_STREET_PARKINGS, {
    variables,
    skip: !currentCity?.id || (bounding && !nearBy),
  });

  const { refetch: refetchOffStreetParking } = useQuery(QUERY_OFF_STREET_PARKING, {
    variables: { cityId: currentCity?.id ?? "", id: "" },
    skip: true,
  });

  useEffect(() => {
    if (loading) {
      showGlobalLoading();
    } else {
      hideGlobalLoading();
    }
  }, [loading]);

  const [createOffStreetParking] = useMutation(QUERY_ADD_OFF_STREET_PARKING, {
    onCompleted: () => {
      refetch();
    },
  });

  const [updateOffStreetParking] = useMutation(QUERY_UPDATE_OFF_STREET_PARKING, {
    onCompleted: () => {
      refetch();
    },
  });

  const [removeOffStreetParking] = useMutation(QUERY_REMOVE_OFF_STREET_PARKING, {
    onCompleted: () => {
      refetch();
    },
  });

  const OffStreetParking = useMemo(() => {
    return data?.offStreetParkings || [];
  }, [data]);

  const fetchOffStreetParking = async (id: string) => {
    const { data } = await refetchOffStreetParking({ cityId: currentCity?.id ?? "", id });
    return data?.offStreetParking;
  };

  return {
    nearBy,
    OffStreetParking,
    loading,
    fetchOffStreetParking,
    createOffStreetParking,
    updateOffStreetParking,
    removeOffStreetParking,
  };
};
